/**
 * The marks the package draws for itself.
 *
 * Inline SVG rather than an icon-library import, for the same reason as
 * intent-icon.tsx: the package depends on no icon library, and which one the
 * apps standardise on is still open.
 *
 * They live here because five components were each drawing their own dismiss
 * cross and two were drawing their own chevron, in two geometries that had
 * already drifted apart. A mark the product uses in five places is one mark.
 *
 * Always decorative. Every one of them sits inside a control that carries the
 * accessible name, so announcing the glyph too would repeat it.
 */
type GlyphSize = 'small' | 'medium'

/**
 * A size name rather than a class.
 *
 * A caller passing a Tailwind class is a `className` prop wearing a different
 * name, which is the one thing this package does not allow.
 */
const BOX: Record<GlyphSize, string> = {
    small: 'size-2.5',
    medium: 'size-3'
}

/** Dismisses whatever encloses it: a dialog, a drawer, a toast, a filled field. */
export const CloseGlyph = ({ size = 'medium' }: { size?: GlyphSize }) => (
    <svg viewBox="0 0 12 12" className={BOX[size]} aria-hidden focusable="false">
        <path d="M1 1l10 10M11 1L1 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
)

/** Opens the list under a Select or a Combobox. */
export const ChevronDownGlyph = () => (
    <svg viewBox="0 0 10 6" className="size-2.5" aria-hidden focusable="false">
        <path
            d="M1 1l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

/** Copies whatever is beside it. */
export const CopyGlyph = ({ size = 'medium' }: { size?: GlyphSize }) => (
    <svg viewBox="0 0 16 16" className={BOX[size]} aria-hidden focusable="false">
        <rect
            x="5.75"
            y="1.75"
            width="8.5"
            height="10.5"
            rx="1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path
            d="M10.25 14.25h-8.5a0 0 0 0 1 0 0V3.75"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

/** Confirms that something just happened. Pairs with CopyGlyph. */
export const CheckGlyph = ({ size = 'medium' }: { size?: GlyphSize }) => (
    <svg viewBox="0 0 16 16" className={BOX[size]} aria-hidden focusable="false">
        <path
            d="M2.75 8.5 6.25 12l7-8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

/**
 * Marks a link that leaves for another site or another tab.
 *
 * Sized in `em` rather than from the box scale above: it sits inside running
 * text, so it has to follow whatever size that text is set at. A fixed 12px
 * mark beside 20px type reads as a rendering fault.
 */
export const ExternalGlyph = () => (
    <svg viewBox="0 0 16 16" className="h-[1em] w-[1em] shrink-0" aria-hidden focusable="false">
        <path
            d="M6.25 3.25H3.75v9h9v-2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M9.75 2.75h3.5v3.5M13.25 2.75 7.5 8.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)
